import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { ParsedSpecSheet, SpecSheetGroup } from "../../utils/specSheetUtils";
import { RagMarkdownContent } from "./RagMarkdownContent";

export interface SpecSheetViewProps {
  sheet: ParsedSpecSheet;
}

function LeftoverGroup({ group }: { group: SpecSheetGroup }) {
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 700,
          color: "text.primary",
          mb: 0.5,
        }}
      >
        {group.name}
        {group.qualifier ? (
          <Typography
            component="span"
            sx={{ ml: 0.75, fontWeight: 400, color: "text.secondary", fontSize: 13 }}
          >
            ({group.qualifier})
          </Typography>
        ) : null}
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.25, "& li": { mb: 0.35 } }}>
        {group.values.map((v, i) => (
          <Box component="li" key={i}>
            <Typography sx={{ fontSize: 14, lineHeight: 1.5, color: "text.primary" }}>
              {v.key ? (
                <>
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {v.key}:
                  </Box>{" "}
                  {v.value}
                </>
              ) : (
                v.value
              )}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/**
 * Compact specification sheet: pivoted value table, singleton callouts, notes.
 * Styling aligns with existing RagTablesSection / TableCard tokens.
 */
export function SpecSheetView({ sheet }: SpecSheetViewProps) {
  return (
    <Box>
      {sheet.intro ? <RagMarkdownContent text={sheet.intro} /> : null}

      {sheet.sectionTitle ? (
        <Typography
          sx={{
            mt: sheet.intro ? 1.5 : 0,
            mb: 1,
            fontSize: { xs: 15, sm: 16 },
            fontWeight: 700,
            color: "text.primary",
            lineHeight: 1.35,
          }}
        >
          {sheet.sectionTitle}
        </Typography>
      ) : null}

      {sheet.table ? (
        <Box
          sx={{
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
            bgcolor: "background.paper",
          }}
        >
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: { xs: 280, sm: 360 } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.800" }}>
                  <TableCell
                    sx={{
                      color: "common.white",
                      fontWeight: 600,
                      borderColor: "divider",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {sheet.table.rowHeader}
                  </TableCell>
                  {sheet.table.columnHeaders.map((h) => (
                    <TableCell
                      key={h}
                      align="right"
                      sx={{
                        color: "common.white",
                        fontWeight: 600,
                        borderColor: "divider",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sheet.table.rows.map((row, ri) => (
                  <TableRow
                    key={row.label}
                    sx={{
                      bgcolor: ri % 2 === 0 ? "background.paper" : "grey.50",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <TableCell
                      sx={{
                        borderColor: "divider",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.label}
                    </TableCell>
                    {row.cells.map((cell, ci) => (
                      <TableCell
                        key={ci}
                        align="right"
                        sx={{
                          borderColor: "divider",
                          fontWeight: 500,
                          fontVariantNumeric: "tabular-nums",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {cell || "—"}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      ) : null}

      {sheet.callouts.map((c) => (
        <Typography
          key={`${c.label}-${c.value}`}
          sx={{
            mt: 1.5,
            fontSize: 14,
            lineHeight: 1.5,
            color: "text.primary",
          }}
        >
          <Box component="span" sx={{ fontWeight: 700 }}>
            {c.label}:
          </Box>{" "}
          {c.value}
          {c.qualifier ? (
            <Box component="span" sx={{ color: "text.secondary" }}>
              {" "}
              ({c.qualifier})
            </Box>
          ) : null}
        </Typography>
      ))}

      {sheet.leftoverGroups.map((g) => (
        <LeftoverGroup key={g.name} group={g} />
      ))}

      {sheet.notes.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: "text.primary",
              mb: 0.75,
            }}
          >
            {sheet.notesTitle || "Notes"}
          </Typography>
          <Box
            sx={{
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "grey.50",
              px: 1.5,
              py: 1.25,
            }}
          >
            <Box component="ul" sx={{ m: 0, pl: 2.25, "& li": { mb: 0.75 } }}>
              {sheet.notes.map((n, i) => (
                <Box component="li" key={i}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      lineHeight: 1.55,
                      color: "text.secondary",
                    }}
                  >
                    {n}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      ) : null}

      {sheet.trailingProse ? (
        <Box sx={{ mt: 1.5 }}>
          <RagMarkdownContent text={sheet.trailingProse} />
        </Box>
      ) : null}
    </Box>
  );
}
